import { Check, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { Member } from "@/features/team/api";
import { cn } from "@/lib/utils";

interface MemberMultiSelectProps {
  /** Assignable team members, already filtered (e.g. to active) by the caller.
   * Only id/name/email are read, so a minimal member shape is accepted. */
  members: Pick<Member, "id" | "name" | "email">[];
  /** Currently-selected member ids. */
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Trigger label when nothing is selected. */
  placeholder?: string;
  className?: string;
  triggerSize?: "sm" | "default";
}

/**
 * Compact team-member picker: a popover of checkable members. Used to assign a
 * client to team members on the add-client form and the client team card.
 */
export function MemberMultiSelect({
  members,
  selected,
  onChange,
  disabled,
  placeholder = "Team members",
  className,
  triggerSize = "sm",
}: MemberMultiSelectProps) {
  const selectedSet = new Set(selected);

  function toggle(id: string) {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  }

  const count = selected.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={triggerSize}
          disabled={disabled}
          className={cn("gap-1.5", className)}
        >
          <Users className="size-4" />
          {count > 0 ? `${placeholder} (${count})` : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        {members.length === 0 ? (
          <p className="text-muted-foreground p-4 text-sm">No team members available.</p>
        ) : (
          <div className="max-h-72 overflow-y-auto p-1">
            {members.map((member) => {
              const isSelected = selectedSet.has(member.id);
              return (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => toggle(member.id)}
                  className="hover:bg-muted flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm"
                >
                  <span
                    className={cn(
                      "flex size-4 items-center justify-center rounded-sm border",
                      isSelected
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-input",
                    )}
                  >
                    {isSelected && <Check className="size-3" />}
                  </span>
                  <span className="truncate">{member.name || member.email}</span>
                </button>
              );
            })}
          </div>
        )}
        {count > 0 && (
          <div className="border-t p-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={() => onChange([])}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
